import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Org, Stage } from '@figureit/shared';
import { api } from '../api/client';
import { useUpdateLead } from '../api/queries';
import { Modal } from './ui/Modal';

/**
 * Mandatory prompt when moving a lead to a Won/Lost stage. The API enforces this
 * too — the prompt just gives the user a friendly UI for it.
 */
export function WonLostPrompt({
  leadId,
  targetStage,
  onClose,
  onDone,
}: {
  leadId: string;
  targetStage: Stage;
  onClose: () => void;
  onDone: () => void;
}) {
  const { data: org } = useQuery({
    queryKey: ['org'],
    queryFn: () => api<Org>('/api/org'),
  });
  const update = useUpdateLead();
  const [reason, setReason] = useState('');
  const [custom, setCustom] = useState('');

  const presets = (targetStage.isWon ? org?.wonReasons : org?.lostReasons) ?? [];

  // Default to the first preset for one-click confirm.
  useEffect(() => {
    if (presets[0] && !reason) setReason(presets[0]);
  }, [presets, reason]);

  const isWon = targetStage.isWon;
  const heading = isWon ? '🏆 Mark as won' : '👋 Mark as lost';
  const fieldKey = isWon ? 'wonReason' : 'lostReason';

  const finalReason = reason === '__custom__' ? custom.trim() : reason;
  const canSubmit = !!finalReason;

  const submit = () => {
    if (!canSubmit) return;
    update.mutate(
      {
        id: leadId,
        stageId: targetStage.id,
        [fieldKey]: finalReason,
      } as Parameters<typeof update.mutate>[0],
      { onSuccess: onDone },
    );
  };

  return (
    <Modal
      onClose={onClose}
      width="sm"
      zIndex="z-[70]"
      title={heading}
      footer={
        <>
          <button className="btn btn-secondary flex-1 sm:flex-none" onClick={onClose}>
            Cancel
          </button>
          <button
            className={clsx('flex-1 sm:flex-none', isWon ? 'btn btn-primary' : 'btn btn-danger')}
            onClick={submit}
            disabled={!canSubmit || update.isPending}
          >
            {update.isPending ? 'Saving…' : isWon ? 'Mark as won' : 'Mark as lost'}
          </button>
        </>
      }
    >
      <p className="text-sm text-text3 mb-4">
        {isWon
          ? 'Why did this deal close? This feeds the win-reason breakdown in Reports.'
          : 'Why did this deal not close? This feeds the loss analysis in Reports — be honest, even when the answer stings.'}
      </p>
      <div className="space-y-1.5 mb-3">
        {presets.map((r) => (
          <label
            key={r}
            className="flex items-center gap-3 px-3 py-2.5 min-h-[44px] bg-surface2 hover:bg-surface3 rounded-md cursor-pointer text-sm"
          >
            <input
              type="radio"
              checked={reason === r}
              onChange={() => setReason(r)}
              className="w-auto"
            />
            {r}
          </label>
        ))}
        <label className="flex items-center gap-3 px-3 py-2.5 min-h-[44px] bg-surface2 hover:bg-surface3 rounded-md cursor-pointer text-sm">
          <input
            type="radio"
            checked={reason === '__custom__'}
            onChange={() => setReason('__custom__')}
            className="w-auto"
          />
          Other (specify)
        </label>
        {reason === '__custom__' && (
          <input
            autoFocus
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="Briefly describe…"
            enterKeyHint="done"
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
            }}
          />
        )}
      </div>
    </Modal>
  );
}
